<script setup>
import { ref, onMounted, onUnmounted } from 'vue'
import ProductCard from './ProductCard.vue'

const products = [
  {
    id: 'kannister',
    title: 'Kannister',
    description: 'Seamless integration with your existing CI/CD pipeline. Enforce security policies at every stage from development to production.',
    technicalHighlight: 'CI/CD integration'
  },
  {
    id: 'krate',
    title: 'Krate',
    description: 'Advanced policy enforcement and compliance scanning for development containers. Automatically validate security requirements before deployment.',
    technicalHighlight: 'Policy-as-code engine'
  },
  {
    id: 'enterprise-server',
    title: 'Enterprise Server',
    description: 'Real-time governance and audit logging across your entire development infrastructure. Complete visibility into container usage and access patterns.',
    technicalHighlight: 'Real-time monitoring'
  }
]

const selectedCard = ref(null)

const selectCard = (id, event) => {
  event.stopPropagation()
  selectedCard.value = id
  window.location.hash = id
}

const clearSelection = () => {
  selectedCard.value = null
}

const handleHashChange = () => {
  const hash = window.location.hash.replace('#', '')
  if (hash === 'kannister' || hash === 'krate' || hash === 'enterprise-server') {
    selectedCard.value = hash
  }
}

onMounted(() => {
  window.addEventListener('hashchange', handleHashChange)
  // Check initial hash on mount
  handleHashChange()
})

onUnmounted(() => {
  window.removeEventListener('hashchange', handleHashChange)
})
</script>

<template>
  <section id="products" class="py-20 bg-white" @click="clearSelection">
    <div class="section-container">
      <!-- Section Header -->
      <div class="text-center mb-16">
        <h2 class="text-4xl md:text-5xl font-bold text-kannister-dark-900 mb-4">
          Complete Security for Your <span class="text-kannister-blue-600">Development Lifecycle</span>
        </h2>
      </div>

      <!-- Product Cards Grid -->
      <div class="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
        <div id="kannister" @click="selectCard('kannister', $event)" :class="['product-card bg-white rounded-lg p-6 hover:shadow-lg hover:-translate-y-1 transition-all duration-300 scroll-mt-20 cursor-pointer', selectedCard === 'kannister' ? 'border-4 border-kannister-blue-600 shadow-2xl shadow-kannister-blue-600/50 -translate-y-1' : 'border border-kannister-dark-200']">
          <div class="w-12 h-12 bg-kannister-blue-100 rounded-lg flex items-center justify-center mb-4">
            <svg class="w-7 h-7 text-kannister-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
            </svg>
          </div>
          <h3 class="text-xl font-bold text-kannister-dark-900 mb-3">Kannister</h3>
          <p class="text-kannister-dark-600 mb-4 leading-relaxed">Seamless integration with your existing CI/CD pipeline. Enforce security policies at every stage from development to production.</p>
          <div class="inline-flex items-center space-x-2 text-sm font-medium text-kannister-blue-600 mb-4">
            <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
              <path fill-rule="evenodd" d="M12.316 3.051a1 1 0 01.633 1.265l-4 12a1 1 0 11-1.898-.632l4-12a1 1 0 011.265-.633zM5.707 6.293a1 1 0 010 1.414L3.414 10l2.293 2.293a1 1 0 11-1.414 1.414l-3-3a1 1 0 010-1.414l3-3a1 1 0 011.414 0zm8.586 0a1 1 0 011.414 0l3 3a1 1 0 010 1.414l-3 3a1 1 0 11-1.414-1.414L16.586 10l-2.293-2.293a1 1 0 010-1.414z" clip-rule="evenodd" />
            </svg>
            <span>CI/CD integration</span>
          </div>
        </div>

        <div id="krate" @click="selectCard('krate', $event)" :class="['product-card bg-white rounded-lg p-6 hover:shadow-lg hover:-translate-y-1 transition-all duration-300 scroll-mt-20 cursor-pointer', selectedCard === 'krate' ? 'border-4 border-kannister-blue-600 shadow-2xl shadow-kannister-blue-600/50 -translate-y-1' : 'border border-kannister-dark-200']">
          <div class="w-12 h-12 bg-kannister-blue-100 rounded-lg flex items-center justify-center mb-4">
            <svg class="w-7 h-7 text-kannister-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 12h14M5 12a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v4a2 2 0 01-2 2M5 12a2 2 0 00-2 2v4a2 2 0 002 2h14a2 2 0 002-2v-4a2 2 0 00-2-2m-2-4h.01M17 16h.01" />
            </svg>
          </div>
          <h3 class="text-xl font-bold text-kannister-dark-900 mb-3">Krate</h3>
          <p class="text-kannister-dark-600 mb-4 leading-relaxed">Advanced policy enforcement and compliance scanning for development containers. Automatically validate security requirements before deployment.</p>
          <div class="inline-flex items-center space-x-2 text-sm font-medium text-kannister-blue-600 mb-4">
            <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
              <path fill-rule="evenodd" d="M12.316 3.051a1 1 0 01.633 1.265l-4 12a1 1 0 11-1.898-.632l4-12a1 1 0 011.265-.633zM5.707 6.293a1 1 0 010 1.414L3.414 10l2.293 2.293a1 1 0 11-1.414 1.414l-3-3a1 1 0 010-1.414l3-3a1 1 0 011.414 0zm8.586 0a1 1 0 011.414 0l3 3a1 1 0 010 1.414l-3 3a1 1 0 11-1.414-1.414L16.586 10l-2.293-2.293a1 1 0 010-1.414z" clip-rule="evenodd" />
            </svg>
            <span>Policy-as-code engine</span>
          </div>
        </div>

        <div id="enterprise-server" @click="selectCard('enterprise-server', $event)" :class="['product-card bg-white rounded-lg p-6 hover:shadow-lg hover:-translate-y-1 transition-all duration-300 scroll-mt-20 cursor-pointer', selectedCard === 'enterprise-server' ? 'border-4 border-kannister-blue-600 shadow-2xl shadow-kannister-blue-600/50 -translate-y-1' : 'border border-kannister-dark-200']">
          <div class="w-12 h-12 bg-kannister-blue-100 rounded-lg flex items-center justify-center mb-4">
            <svg class="w-7 h-7 text-kannister-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 7v10c0 2.21 3.582 4 8 4s8-1.79 8-4V7M4 7c0 2.21 3.582 4 8 4s8-1.79 8-4M4 7c0-2.21 3.582-4 8-4s8 1.79 8 4m0 5c0 2.21-3.582 4-8 4s-8-1.79-8-4" />
            </svg>
          </div>
          <h3 class="text-xl font-bold text-kannister-dark-900 mb-3">Enterprise Server</h3>
          <p class="text-kannister-dark-600 mb-4 leading-relaxed">Real-time governance and audit logging across your entire development infrastructure. Complete visibility into container usage and access patterns.</p>
          <div class="inline-flex items-center space-x-2 text-sm font-medium text-kannister-blue-600 mb-4">
            <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
              <path fill-rule="evenodd" d="M12.316 3.051a1 1 0 01.633 1.265l-4 12a1 1 0 11-1.898-.632l4-12a1 1 0 011.265-.633zM5.707 6.293a1 1 0 010 1.414L3.414 10l2.293 2.293a1 1 0 11-1.414 1.414l-3-3a1 1 0 010-1.414l3-3a1 1 0 011.414 0zm8.586 0a1 1 0 011.414 0l3 3a1 1 0 010 1.414l-3 3a1 1 0 11-1.414-1.414L16.586 10l-2.293-2.293a1 1 0 010-1.414z" clip-rule="evenodd" />
            </svg>
            <span>Real-time monitoring</span>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

